import { useTheme } from "@emotion/react";
import React, { useMemo, useState } from "react";
import { MdAdd, MdEdit } from "react-icons/md";
import { useQueryClient } from "react-query";

import { useClientManager } from "@megaron/react-clients";
import { CustomerDoc, isDocOfType } from "@megaron/docs-contracts";
import { TicketStatus } from "@megaron/support-contracts";
import { Uuid } from "@megaron/uuid";
import { Button } from "@dash/form";
import { DocsSelectDialog } from "@dash/select";

import { CrmCustomerCard } from "./CrmCustomerCard";
import { SupportEditTagsDialog } from "./SupportEditTagsDialog";

type Props = {
  ticketId: Uuid;
  subject: string;
  status: TicketStatus;
  priority: "low" | "medium" | "high" | "urgent" | null;
  category: "billing" | "technical" | "sales" | "complaint" | "other" | null;
  reporterEmail: string;
  customerUuids: Uuid[];
  tags: string[];
};

function matchCustomerByEmail(items: CustomerDoc[], email: string): CustomerDoc | undefined {
  const lower = email.trim().toLowerCase();
  return items.find((c) => c.email?.trim().toLowerCase() === lower);
}

export const SupportTicketContactCrmCard: React.FC<Props> = ({
  ticketId,
  subject,
  status,
  priority,
  category,
  reporterEmail,
  customerUuids,
  tags,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);

  const docsRetrieveQuery = useClientManager("docs")
    .retrieveDocs()
    .useQuery(
      { docIds: customerUuids.map((uuid) => `customer:${uuid}`) },
      { enabled: customerUuids.length > 0 },
    );

  const searchQuery = useClientManager("docs")
    .searchCustomers()
    .useQuery(
      { searchText: reporterEmail.trim(), limit: 10 },
      { enabled: customerUuids.length === 0 && !!reporterEmail.trim() },
    );

  const addMutation = useClientManager("support")
    .addTicketCustomer()
    .useMutation({
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] }),
    });

  const removeMutation = useClientManager("support")
    .removeTicketCustomer()
    .useMutation({
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] }),
    });

  const linkedCustomers = useMemo((): CustomerDoc[] => {
    if (customerUuids.length === 0) return [];
    return (docsRetrieveQuery.data ?? []).filter((d): d is CustomerDoc => d !== null && isDocOfType("customer")(d));
  }, [customerUuids.length, docsRetrieveQuery.data]);

  const emailMatchedCustomer = useMemo((): CustomerDoc | undefined => {
    if (customerUuids.length > 0) return undefined;
    return matchCustomerByEmail(searchQuery.data?.items ?? [], reporterEmail);
  }, [customerUuids.length, searchQuery.data?.items, reporterEmail]);

  const isLoading =
    (customerUuids.length > 0 ? docsRetrieveQuery.isLoading : false) ||
    (customerUuids.length === 0 ? searchQuery.isLoading : false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });

  const tagsSection = (
    <div css={{ display: "flex", gap: "7px", alignItems: "center", color: theme.colors.primary, flexWrap: "wrap" }}>
      {tags.map((tag) => (
        <div
          key={tag}
          css={{
            borderRadius: theme.smallBorderRadius,
            background: theme.colors.primaryLight,
            padding: "5px 10px",
            color: theme.colors.primary,
            cursor: "pointer",
          }}
        >
          {`#${tag}`}
        </div>
      ))}
      <Button icon={<MdEdit />} variant="text" onClick={() => setTagsDialogOpen(true)}>
        {tags.length ? "Edytuj" : "Dodaj Tag"}
      </Button>
    </div>
  );

  if (isLoading) {
    return (
      <div css={{ padding: "0.75rem 1rem", border: `1px solid ${theme.colors.border}`, borderRadius: theme.smallBorderRadius, fontSize: "14px", color: theme.colors.secondaryText }}>
        Szukam w CRM...
      </div>
    );
  }

  return (
    <>
      <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {emailMatchedCustomer && <CrmCustomerCard customer={emailMatchedCustomer} />}
        {linkedCustomers.map((customer) => (
          <CrmCustomerCard
            key={customer.uuid}
            customer={customer}
            onDetach={() => removeMutation.mutate({ id: ticketId, customerUuid: customer.uuid })}
          />
        ))}
        {linkedCustomers.length === 0 && !emailMatchedCustomer && (
          <div css={{ padding: "0.75rem 1rem", border: `1px solid ${theme.colors.border}`, borderRadius: theme.smallBorderRadius, fontSize: "14px" }}>
            <span css={{ color: theme.colors.secondaryText }}>Brak w CRM</span>
          </div>
        )}
        <Button icon={<MdAdd />} variant="text" onClick={() => setAddDialogOpen(true)}>
          Przypisz klienta
        </Button>
      </div>
      <DocsSelectDialog
        docType={["customer"]}
        variant="single-select"
        label="Przypisz klienta z CRM"
        noTrigger
        isOpen={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSelect={(option) => {
          if (option && option.docType === "customer" && option.value) {
            setAddDialogOpen(false);
            addMutation.mutate({ id: ticketId, customerUuid: Uuid(option.value) });
          }
        }}
      />
      {tagsSection}
      {tagsDialogOpen && (
        <SupportEditTagsDialog
          onClose={() => setTagsDialogOpen(false)}
          ticketId={ticketId}
          subject={subject}
          status={status}
          priority={priority}
          category={category}
          ticketTags={tags}
          onSuccess={invalidate}
        />
      )}
    </>
  );
};
